import React from 'react';
import cx from 'classnames';

import Checkbox from '../../../Checkbox';
import RadioInput from '../../../RadioInput';
import List from '../../../List';
import Box from '../../../Box';
import Skeleton from '../../../Skeleton';
import Checkmark20 from '../../Icon/library/Checkmark20';

import { Props as BaseListItemProps } from '../../../List/ListItem';

import styles from './ListItem.module.scss';

export interface ListItemProps extends BaseListItemProps {
  /**
   * Specify the `ListItem` appearance type.
   */
  appearance?: 'checkbox' | 'radio';
  /**
   * If true, component will be disabled.
   * @default false
   */
  disabled?: boolean;
  /** Apply `id` to the components container. */
  id: string;
  /**
   * If true, the succuss indicator will be visible.
   * @default false
   */
  isFulfilled?: boolean;
  /**
   * If true, a loading indicator will be visible.
   * @default false
   */
  isLoading?: boolean;
  /**	Function that will be called when the component is clicked. */
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** The value attribute for the input. */
  value: boolean;
}

export const ListItem: React.FC<ListItemProps> = ({
  appearance = 'checkbox',
  disabled,
  id,
  isFulfilled,
  isLoading,
  onChange,
  value,
  ...restProps
}) => {
  const isCheckbox = appearance === 'checkbox';
  const isRadio = appearance === 'radio';

  const Component = isCheckbox ? Checkbox : RadioInput;

  return (
    <Box
      className={cx(
        styles.listItem,
        disabled && styles.isDisabled,
        isLoading && styles.isLoading,
        isFulfilled && styles.isFulfilled,
        isRadio && styles.radio,
        isCheckbox && styles.checkbox,
      )}
    >
      {isLoading ? (
        <Box display='flex' alignItems='center' gap={1}>
          {isFulfilled && (
            <Box display='flex'>
              <Checkmark20 />
            </Box>
          )}
          <Skeleton effect='sheen' className={styles.skeleton} />
        </Box>
      ) : (
        <Component
          id={id}
          onChange={onChange}
          value={value}
          labelClassName={styles.label}
          disabled={disabled}
          className={cx(styles.inputContainer, disabled && styles.isDisabled)}
          inputClassName={styles.input}
        >
          <List.Item hasPadding={false} {...restProps} />
        </Component>
      )}
    </Box>
  );
};

ListItem.displayName = 'ListItem';
export default ListItem;
export { styles };
